import React, { useState, useEffect } from 'react';
import {
    FiActivity, FiUsers, FiBookOpen, FiCalendar, FiAlertTriangle,
    FiCheckCircle, FiTrendingUp, FiZap, FiSettings, FiRefreshCw,
    FiClock, FiTarget, FiBarChart2
} from 'react-icons/fi';
import {
    getInstructors, getCourses, getSchedules,
    getCalendarConfig, updateCalendarConfig,
    calculateInstructorLoad, calculateDayCapacity,
    evaluateRulesForCourse, evaluateRulesForInstructor
} from '../services';
import { useGoogleCalendar } from '../../../contexts/GoogleCalendarContext';
import GoogleCalendarConnect from '../components/GoogleCalendarConnect';
import toast from 'react-hot-toast';

const MODOS_SISTEMA = [
    { value: 'expansion', label: 'Expansión', color: 'bg-green-500', description: 'Máxima venta, más masterclass' },
    { value: 'equilibrio', label: 'Equilibrio', color: 'bg-blue-500', description: 'Balance venta/servicio' },
    { value: 'estabilidad', label: 'Estabilidad', color: 'bg-gray-500', description: 'Priorizar operación' },
];

const DashboardPage = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // Google Calendar
    const {
        isConnected: gcalConnected,
        googleEvents,
    } = useGoogleCalendar();

    // Data
    const [instructors, setInstructors] = useState([]);
    const [courses, setCourses] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [config, setConfig] = useState(null);

    // Calculated metrics
    const [metrics, setMetrics] = useState({
        totalInstructors: 0,
        activeInstructors: 0,
        saturatedInstructors: 0,
        totalCourses: 0,
        coursesByFase: {},
        todayEvents: 0,
        weekEvents: 0,
        todayCapacity: null,
        recommendations: [],
        alerts: []
    });

    // Modal
    const [isConfigOpen, setIsConfigOpen] = useState(false);
    const [configForm, setConfigForm] = useState({
        modoSistema: 'equilibrio',
        maxMasterclassDiario: 5,
        maxEventosDiario: 10,
        maxMasterclassPorAsesor: 3,
        alertaSaturacion: 80
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [instData, courseData, scheduleData, configData] = await Promise.all([
                getInstructors(),
                getCourses(),
                getSchedules(),
                getCalendarConfig()
            ]);

            setInstructors(instData);
            setCourses(courseData);
            setSchedules(scheduleData);
            setConfig(configData);

            if (configData) {
                setConfigForm({
                    modoSistema: configData.modoSistema || 'equilibrio',
                    maxMasterclassDiario: configData.maxMasterclassDiario || 5,
                    maxEventosDiario: configData.maxEventosDiario || 10,
                    maxMasterclassPorAsesor: configData.maxMasterclassPorAsesor || 3,
                    alertaSaturacion: configData.alertaSaturacion || 80
                });
            }

            // Calculate metrics
            await calculateMetrics(instData, courseData, scheduleData);

        } catch (error) {
            console.error(error);
            toast.error('Error cargando datos del dashboard');
        } finally {
            setLoading(false);
        }
    };

    const refreshData = async () => {
        setRefreshing(true);
        await loadData();
        setRefreshing(false);
        toast.success('Datos actualizados');
    };

    const calculateMetrics = async (insts, crses, scheds) => {
        const now = new Date();
        const today = now.toISOString().split('T')[0];

        // Instructor metrics
        let saturated = 0;
        let active = 0;
        const recommendations = [];
        const alerts = [];

        for (const inst of insts) {
            const load = await calculateInstructorLoad(inst.id);
            const isSaturated = load.cursosActivos >= (inst.maxCursos || 4);

            if (isSaturated) {
                saturated++;
                alerts.push({
                    type: 'instructor',
                    severity: 'high',
                    message: `${inst.name} está saturado (${load.cursosActivos}/${inst.maxCursos || 4} cursos)`
                });
            }

            if (inst.estado === 'activo') active++;

            // Check for instructor recommendations
            const { recomendaciones } = await evaluateRulesForInstructor(inst);
            recomendaciones.forEach(rec => {
                recommendations.push({
                    type: 'instructor',
                    entity: inst.name,
                    ...rec
                });
            });
        }

        // Course metrics
        const coursesByFase = {};
        for (const course of crses) {
            const fase = course.fase || 'pre_lanzamiento';
            coursesByFase[fase] = (coursesByFase[fase] || 0) + 1;

            // Check for course recommendations
            const { recomendaciones } = evaluateRulesForCourse(course);
            recomendaciones.forEach(rec => {
                recommendations.push({
                    type: 'course',
                    entity: course.name,
                    ...rec
                });
            });

            // Alert for low performance courses in launch phase
            if ((course.fase === 'lanzamiento' || course.fase === 'pre_lanzamiento') &&
                course.rendimientoPublicidad < 30) {
                alerts.push({
                    type: 'course',
                    severity: 'medium',
                    message: `${course.name} tiene bajo rendimiento (${course.rendimientoPublicidad}%) en fase de lanzamiento`
                });
            }
        }

        // Schedule metrics
        const todayStart = new Date(today);
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);

        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() + 7);

        const todayEvents = scheds.filter(s => {
            const start = new Date(s.start);
            return start >= todayStart && start <= todayEnd;
        }).length;

        const weekEvents = scheds.filter(s => {
            const start = new Date(s.start);
            return start >= now && start <= weekEnd;
        }).length;

        // Today capacity
        const todayCapacity = await calculateDayCapacity(today);

        if (todayCapacity.saturado) {
            alerts.push({
                type: 'capacity',
                severity: 'high',
                message: 'Capacidad del día SATURADA - No programar más eventos'
            });
        } else if (todayCapacity.alerta) {
            alerts.push({
                type: 'capacity',
                severity: 'medium',
                message: `Capacidad del día al ${Math.round((todayCapacity.eventosTotales / 10) * 100)}%`
            });
        }

        // Google Calendar conflict detection
        if (gcalConnected && googleEvents.length > 0) {
            const todayGcalEvents = googleEvents.filter(ge => {
                const start = new Date(ge.start);
                return start >= todayStart && start <= todayEnd;
            });

            const todayInternalEvents = scheds.filter(s => {
                const start = new Date(s.start);
                return start >= todayStart && start <= todayEnd;
            });

            for (const internal of todayInternalEvents) {
                for (const gcal of todayGcalEvents) {
                    const iStart = new Date(internal.start);
                    const iEnd = new Date(internal.end);
                    const gStart = new Date(gcal.start);
                    const gEnd = new Date(gcal.end);
                    if (iStart < gEnd && iEnd > gStart) {
                        alerts.push({
                            type: 'google_calendar',
                            severity: 'medium',
                            message: `Conflicto: "${internal.title}" se solapa con "${gcal.title}" (Google Calendar)`
                        });
                    }
                }
            }
        }

        setMetrics({
            totalInstructors: insts.length,
            activeInstructors: active,
            saturatedInstructors: saturated,
            totalCourses: crses.length,
            coursesByFase,
            todayEvents,
            weekEvents,
            todayCapacity,
            recommendations,
            alerts
        });
    };

    const handleSaveConfig = async () => {
        try {
            await updateCalendarConfig(configForm);
            setConfig({ ...config, ...configForm });
            toast.success('Configuración guardada');
            setIsConfigOpen(false);
        } catch (error) {
            toast.error('Error guardando configuración');
        }
    };

    const getModoConfig = (modo) => {
        return MODOS_SISTEMA.find(m => m.value === modo) || MODOS_SISTEMA[1];
    };

    const getCapacityColor = (capacity) => {
        if (!capacity) return 'bg-gray-200';
        const percentage = (capacity.eventosTotales / 10) * 100;
        if (percentage >= 100) return 'bg-red-500';
        if (percentage >= 80) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    if (loading) {
        return (
            <div className="h-full bg-white dark:bg-[#13161c]">
                <div className="fixed top-0 left-0 w-full h-1 z-50 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-loading-bar" />
                </div>
            </div>
        );
    }

    const modoActual = getModoConfig(config?.modoSistema || 'equilibrio');

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FiActivity className="text-purple-600" />
                        Dashboard Operativo
                    </h1>
                    <p className="text-gray-500">Centro de control del sistema de planificación</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={refreshData}
                        className={`p-2 text-gray-500 hover:bg-gray-100 rounded-full transition ${refreshing ? 'animate-spin' : ''}`}
                        title="Actualizar"
                    >
                        <FiRefreshCw />
                    </button>
                    <button
                        onClick={() => setIsConfigOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                    >
                        <FiSettings /> Configurar
                    </button>
                </div>
            </div>

            {/* Modo del Sistema */}
            <div className={`${modoActual.color} text-white p-4 rounded-xl mb-6 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                    <FiZap size={24} />
                    <div>
                        <p className="text-sm opacity-90">Modo del Sistema</p>
                        <p className="text-xl font-bold">{modoActual.label}</p>
                    </div>
                </div>
                <p className="text-sm opacity-90">{modoActual.description}</p>
            </div>

            {/* Alertas */}
            {metrics.alerts.length > 0 && (
                <div className="mb-6 space-y-2">
                    {metrics.alerts.map((alert, idx) => (
                        <div
                            key={idx}
                            className={`p-4 rounded-lg flex items-center gap-3 ${alert.severity === 'high'
                                    ? 'bg-red-50 border border-red-200 text-red-800'
                                    : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                                }`}
                        >
                            <FiAlertTriangle className={alert.severity === 'high' ? 'text-red-500' : 'text-yellow-500'} />
                            <span className="font-medium">{alert.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {/* Instructores */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <FiUsers className="text-blue-500" size={24} />
                        <span className={`text-xs px-2 py-1 rounded-full ${metrics.saturatedInstructors > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                            {metrics.saturatedInstructors > 0 ? `${metrics.saturatedInstructors} saturados` : 'OK'}
                        </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{metrics.totalInstructors}</p>
                    <p className="text-sm text-gray-500">Instructores</p>
                    <div className="mt-2 text-xs text-gray-400">
                        {metrics.activeInstructors} activos
                    </div>
                </div>

                {/* Cursos */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <FiBookOpen className="text-purple-500" size={24} />
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                            {metrics.coursesByFase['lanzamiento'] || 0} lanzando
                        </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{metrics.totalCourses}</p>
                    <p className="text-sm text-gray-500">Cursos</p>
                    <div className="mt-2 text-xs text-gray-400">
                        {metrics.coursesByFase['activo'] || 0} activos • {metrics.coursesByFase['mixto'] || 0} mixtos
                    </div>
                </div>

                {/* Eventos Hoy */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <FiCalendar className="text-green-500" size={24} />
                        <span className={`text-xs px-2 py-1 rounded-full ${getCapacityColor(metrics.todayCapacity)} text-white`}>
                            {metrics.todayCapacity ? `${metrics.todayCapacity.disponibleTotal} disp.` : '-'}
                        </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{metrics.todayEvents}</p>
                    <p className="text-sm text-gray-500">Eventos Hoy</p>
                    <div className="mt-2 text-xs text-gray-400">
                        {metrics.weekEvents} esta semana
                    </div>
                </div>

                {/* Recomendaciones */}
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <FiTarget className="text-orange-500" size={24} />
                        {metrics.recommendations.length > 0 && (
                            <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 animate-pulse">
                                Pendientes
                            </span>
                        )}
                    </div>
                    <p className="text-2xl font-bold text-gray-800">{metrics.recommendations.length}</p>
                    <p className="text-sm text-gray-500">Recomendaciones IA</p>
                    <div className="mt-2 text-xs text-gray-400">
                        Acciones sugeridas
                    </div>
                </div>
            </div>

            {/* Google Calendar Integration */}
            <div className="mb-6">
                <GoogleCalendarConnect />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Capacidad del Día */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <FiBarChart2 className="text-blue-500" /> Capacidad del Día
                    </h3>

                    {metrics.todayCapacity && (
                        <div className="space-y-4">
                            {/* Barra de capacidad total */}
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">Eventos Totales</span>
                                    <span className="font-bold">{metrics.todayCapacity.eventosTotales}/10</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-4">
                                    <div
                                        className={`h-4 rounded-full transition-all ${getCapacityColor(metrics.todayCapacity)}`}
                                        style={{ width: `${Math.min((metrics.todayCapacity.eventosTotales / 10) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Barra de masterclass */}
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">Masterclass</span>
                                    <span className="font-bold text-purple-700">{metrics.todayCapacity.masterclassProgramadas}/5</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="h-3 rounded-full bg-purple-500 transition-all"
                                        style={{ width: `${Math.min((metrics.todayCapacity.masterclassProgramadas / 5) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Métricas adicionales */}
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                                <div className="text-center p-3 bg-green-50 rounded-lg">
                                    <p className="text-2xl font-bold text-green-700">{metrics.todayCapacity.disponibleTotal}</p>
                                    <p className="text-xs text-green-600">Slots disponibles</p>
                                </div>
                                <div className="text-center p-3 bg-purple-50 rounded-lg">
                                    <p className="text-2xl font-bold text-purple-700">{metrics.todayCapacity.disponibleMasterclass}</p>
                                    <p className="text-xs text-purple-600">MC disponibles</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Recomendaciones de la IA */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <FiZap className="text-orange-500" /> Recomendaciones IA
                    </h3>

                    {metrics.recommendations.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                            <FiCheckCircle size={48} className="mx-auto mb-2 text-green-400" />
                            <p>Todo en orden, sin recomendaciones pendientes</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-h-80 overflow-y-auto">
                            {metrics.recommendations.map((rec, idx) => (
                                <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                                    <div className="flex items-start gap-2">
                                        <FiAlertTriangle className="text-orange-500 mt-0.5" size={14} />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-orange-800">{rec.entity}</p>
                                            <p className="text-xs text-orange-600">{rec.mensaje}</p>
                                        </div>
                                        {rec.accion && (
                                            <button className="text-xs px-2 py-1 bg-orange-200 text-orange-800 rounded hover:bg-orange-300 transition">
                                                Aplicar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Estado de Instructores */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <FiUsers className="text-blue-500" /> Estado de Instructores
                    </h3>

                    <div className="space-y-3">
                        {instructors.slice(0, 5).map(inst => {
                            const isSaturated = inst.estado === 'saturado';
                            return (
                                <div key={inst.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                                        style={{ backgroundColor: inst.color || '#6b7280' }}
                                    >
                                        {inst.name.charAt(0)}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-800">{inst.name}</p>
                                        <p className="text-xs text-gray-500">
                                            {inst.maxCursos || 4} cursos máx • {inst.maxEventosDia || 3} eventos/día
                                        </p>
                                    </div>
                                    <span className={`text-xs px-2 py-1 rounded-full ${isSaturated ? 'bg-red-100 text-red-700' :
                                            inst.estado === 'activo' ? 'bg-green-100 text-green-700' :
                                                'bg-gray-100 text-gray-700'
                                        }`}>
                                        {inst.estado || 'activo'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Cursos por Fase */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <FiTrendingUp className="text-purple-500" /> Cursos por Fase
                    </h3>

                    <div className="space-y-3">
                        {[
                            { key: 'pre_lanzamiento', label: 'Pre-Lanzamiento', color: 'bg-gray-500', icon: '🎯' },
                            { key: 'lanzamiento', label: 'Lanzamiento', color: 'bg-purple-500', icon: '🚀' },
                            { key: 'activo', label: 'Activo', color: 'bg-green-500', icon: '✅' },
                            { key: 'mixto', label: 'Mixto', color: 'bg-blue-500', icon: '🔄' },
                            { key: 'maduro', label: 'Maduro', color: 'bg-yellow-500', icon: '🌟' },
                        ].map(fase => {
                            const count = metrics.coursesByFase[fase.key] || 0;
                            const percentage = metrics.totalCourses > 0 ? (count / metrics.totalCourses) * 100 : 0;

                            return (
                                <div key={fase.key} className="flex items-center gap-3">
                                    <span className="text-lg">{fase.icon}</span>
                                    <div className="flex-1">
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-gray-600">{fase.label}</span>
                                            <span className="font-bold">{count}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${fase.color} transition-all`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Config Modal */}
            {isConfigOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <FiSettings /> Configuración del Sistema
                        </h2>

                        <div className="space-y-4">
                            {/* Modo del Sistema */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Modo del Sistema</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {MODOS_SISTEMA.map(modo => (
                                        <button
                                            key={modo.value}
                                            type="button"
                                            onClick={() => setConfigForm({ ...configForm, modoSistema: modo.value })}
                                            className={`p-3 rounded-lg border-2 transition-all text-center ${configForm.modoSistema === modo.value
                                                    ? `border-blue-500 ${modo.color} text-white`
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <p className="text-sm font-bold">{modo.label}</p>
                                            <p className={`text-[10px] ${configForm.modoSistema === modo.value ? 'text-white opacity-90' : 'text-gray-500'}`}>
                                                {modo.description}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Límites */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Max Masterclass/Día</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="20"
                                        value={configForm.maxMasterclassDiario}
                                        onChange={e => setConfigForm({ ...configForm, maxMasterclassDiario: Number(e.target.value) })}
                                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Max Eventos/Día</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="30"
                                        value={configForm.maxEventosDiario}
                                        onChange={e => setConfigForm({ ...configForm, maxEventosDiario: Number(e.target.value) })}
                                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Max MC por Asesor</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={configForm.maxMasterclassPorAsesor}
                                        onChange={e => setConfigForm({ ...configForm, maxMasterclassPorAsesor: Number(e.target.value) })}
                                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Alerta Saturación (%)</label>
                                    <input
                                        type="number"
                                        min="50"
                                        max="100"
                                        value={configForm.alertaSaturacion}
                                        onChange={e => setConfigForm({ ...configForm, alertaSaturacion: Number(e.target.value) })}
                                        className="mt-1 w-full px-4 py-2 border rounded-lg"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                            <button
                                onClick={() => setIsConfigOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveConfig}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
